import React from 'react'
import {
  Typography,
  List,
  Box,
  Grid
} from '@mui/material'
import { useSessionContext } from 'session'
import type { LegislativeRoles, MemberRole, StaffRole, CommitteeMember, CommitteeStaffRole, LeadershipStaffRole } from 'services/person/us/edit/legislativeRolesService'
import { type LeadershipOfficer } from 'services/leadership/leadershipOfficerListService'
import { RoleItem } from './role-item'
import { RoleSection } from '../role-section/role-section'
import { getOfficeFromLeadershipStaffRole, getMemberFromStaffRole } from 'helpers/person/personHelper'

interface Props {
  personId: number
  roles: LegislativeRoles | null
}

interface LegRoleListSection<T> {
  id: string
  title: string
  data: T[]
  render: (item: T) => React.ReactNode
}

type RedirectHandler = (roleType: string, roleId: number, personId: number) => void

const buildRoleSections = (
  roles: LegislativeRoles,
  personId: number,
  handleRedirect: RedirectHandler
): Array<LegRoleListSection<any>> => {
  const roleSections: Array<LegRoleListSection<any>> = []

  // Member roles
  if (roles.member_roles.length > 0) {
    roleSections.push({
      id: 'member_roles',
      title: 'Member Roles',
      data: roles.member_roles,
      render: (role: MemberRole) => (
        <RoleItem
          key={role.id}
          id={role.id}
          primary={
            (role.state != null)
              ? `${role.legrole.descLong} from ${role.state.desc ?? ''}${(role.district != null) ? ` District ${role.district.district_code}` : ''}`
              : role.legrole.descLong
          }
          redirectHandler={() => { handleRedirect('member', role.id, personId) }}
        />
      )
    })
  }

  // Member staff roles
  if (roles.member_staff_roles.length > 0) {
    roleSections.push({
      id: 'member_staff_roles',
      title: 'Member Staff Roles',
      data: roles.member_staff_roles,
      render: (role: StaffRole) => (
        <RoleItem
          key={role.id}
          id={role.id}
          primary={getMemberFromStaffRole(role) !== '' ? `Office of ${getMemberFromStaffRole(role)}` : 'Member Office'}
          secondary={role.legrole.descLong}
          redirectHandler={() => { handleRedirect('member', role.member?.id ?? 0, role.member?.person_id ?? 0) }}
        />
      )
    })
  }

  // Committee staff roles
  if (roles.committee_staff_roles.length > 0) {
    roleSections.push({
      id: 'committee_staff_roles',
      title: 'Committee Staff Roles',
      data: roles.committee_staff_roles,
      render: (role: CommitteeStaffRole) => (
        <RoleItem
          key={role.id}
          id={role.id}
          primary={`${role.committee?.name ?? ''}`}
          secondary={`${role.legrole.descLong !== undefined ? String(role.legrole.descLong) : ''} ${role.majMin !== undefined && role.majMin !== null ? `(${String(role.majMin)})` : ''}`}
          redirectHandler={() => {
            if (role.committee?.id !== undefined && role.committee.id !== null && role.committee.id !== 0) {
              handleRedirect('committee', role.committee.id, personId)
            }
          }}
        />
      )
    })
  }

  // Committee memberships
  if ((roles.committee_members?.length ?? 0) > 0) {
    roleSections.push({
      id: 'committee_members',
      title: 'Committee Memberships',
      data: roles.committee_members ?? [],
      render: (member: CommitteeMember) => (
        <RoleItem
          key={member.id}
          id={member.id}
          primary={`${member.committee?.name ?? ''} ${(member.role?.code !== undefined && member.role.code !== null && member.role.code !== '') ? `(${member.role.code})` : ''}`}
          redirectHandler={() => {
            if (member.committee?.id !== undefined && member.committee.id !== null && member.committee.id !== 0) {
              handleRedirect('committee', member.committee.id, personId)
            }
          }}
        />
      )
    })
  }

  // Caucus memberships
  if ((roles.caucus_members?.length ?? 0) > 0) {
    roleSections.push({
      id: 'caucus_members',
      title: 'Caucus Memberships',
      data: roles.caucus_members ?? [],
      render: (member: CommitteeMember) => (
        <RoleItem
          key={member.id}
          id={member.id}
          primary={`${member.committee?.name ?? ''} ${(member.role?.code !== undefined && member.role.code !== null && member.role.code !== '') ? `(${member.role.code})` : ''}`}
          redirectHandler={() => {
            if (member.committee?.id !== undefined && member.committee.id !== null && member.committee.id !== 0) {
              handleRedirect('committee', member.committee.id, personId)
            }
          }}
        />
      )
    })
  }

  // Leadership roles
  if ((roles.leadership_roles?.length ?? 0) > 0) {
    roleSections.push({
      id: 'leadership_roles',
      title: 'Leadership Roles',
      data: roles.leadership_roles ?? [],
      render: (role: LeadershipOfficer) => (
        <RoleItem
          key={role.id}
          id={role.id}
          primary={role.ofcname ?? ''}
          secondary={role.role?.desc ?? ''}
          redirectHandler={() => { handleRedirect('leadership', role.id, personId) }}
        />
      )
    })
  }

  // Leadership staff roles
  if ((roles.leadership_staff_roles?.length ?? 0) > 0) {
    roleSections.push({
      id: 'leadership_staff_roles',
      title: 'Leadership Staff Roles',
      data: roles.leadership_staff_roles ?? [],
      render: (role: LeadershipStaffRole) => {
        return (
          <RoleItem
            key={role.id}
            id={role.id}
            primary={getOfficeFromLeadershipStaffRole(role)}
            secondary={role.legrole?.descLong ?? ''}
            redirectHandler={() => {
              if (role.leadership_officer?.id !== undefined) {
                handleRedirect('leadership', role.leadership_officer.id, personId)
              }
            }}
          />
        )
      }
    })
  }

  return roleSections
}

export const PersonLegislativeRolesBlock: React.FC<Props> = ({ personId, roles }) => {
  const { openNewTab } = useSessionContext()

  const handleRedirect: RedirectHandler = (roleType, roleId, personIdArg) => {
    if (roleType === 'member') {
      openNewTab(`/us/editMember/${roleId}/${personIdArg}`)
    } else if (roleType === 'committee') {
      openNewTab(`/us/committee/edit/${roleId}`)
    } else if (roleType === 'leadership') {
      openNewTab(`/us/leadershipOfficers/edit/${roleId}`)
    }
  }

  const roleSections = roles !== null
    ? buildRoleSections(roles, personId, handleRedirect)
    : []

  return (
    <RoleSection
      title="Legislative Branch Roles"
      isLoading={roles === null}
      isEmpty={roleSections.length === 0}
      loadingMessage="Loading legislative roles data..."
      emptyMessage="No legislative roles found for this person."
    >
      <Grid container spacing={2}>
        {roleSections.map((section, index) => (
          <React.Fragment key={section.id}>
            <Grid item xs={12}>
              <Box mb={index < roleSections.length - 1 ? 2 : 0}>
                <Typography variant="subtitle1" gutterBottom>{section.title}</Typography>
                <List>
                  {section.data.map((item) => section.render(item))}
                </List>
              </Box>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </RoleSection>
  )
}
